import React, { useState } from "react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { Button } from "@/shared/components/ui/button";
import { createPageUrl } from "@/shared/utils";
import { auth } from "@/shared/api/graphqlClient";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Bell,
  CheckCircle2,
  AlertTriangle,
  Info,
  ArrowDownToLine,
  ArrowUpFromLine,
  X,
  Loader2,
} from "lucide-react";
import { useLanguage } from "./LanguageContext";
import { getMyNotifications, markNotificationRead, removeNotification as apiRemoveNotification } from "@/shared/api/notifications";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const typeConfig = {
  success: { icon: CheckCircle2, color: "text-emerald-500", bg: "bg-emerald-50" },
  warning: { icon: AlertTriangle, color: "text-amber-500", bg: "bg-amber-50" },
  info: { icon: Info, color: "text-blue-500", bg: "bg-blue-50" },
  deposit: { icon: ArrowDownToLine, color: "text-blue-500", bg: "bg-blue-50" },
  withdrawal: { icon: ArrowUpFromLine, color: "text-emerald-500", bg: "bg-emerald-50" },
};

function mapFromApi(n) {
  return {
    id: n.id,
    title: n.title,
    message: n.message,
    type: n.type,
    read: n.isRead,
    time: new Date(n.createdAt),
  };
}

function normalizeType(value) {
  return String(value || "").toUpperCase();
}

export default function NotificationDropdown({ accentColor = "blue", className }) {
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const userRole = auth.getUserRole()?.toUpperCase();
  const isMerchantRole = userRole === "MERCHANT" || userRole === "STAFF_MERCHANT";
  const allowedMerchantTypes = new Set(["DEPOSIT", "WITHDRAWAL", "SYSTEM"]);

  const { data, isLoading: loading } = useQuery({
    queryKey: ["myNotifications"],
    queryFn: async () => {
      const list = await getMyNotifications();
      const mapped = (list || []).map(mapFromApi);
      if (!isMerchantRole) return mapped;

      return mapped.filter((notification) =>
        allowedMerchantTypes.has(normalizeType(notification.type))
      );
    },
    refetchOnWindowFocus: true,
  });

  const notifications = data || [];
  const unreadNotifications = notifications
    .filter((n) => !n.read)
    .sort((a, b) => b.time.getTime() - a.time.getTime())
    .slice(0, 5); // Show only 5 most recent unread notifications
  const unreadCount = notifications.filter((n) => !n.read).length; // Still show total count in badge

  const markAsRead = async (id) => {
    try {
      await markNotificationRead(id);
      queryClient.invalidateQueries({ queryKey: ["myNotifications"] });
    } catch (_) { }
  };

  const removeNotification = async (id) => {
    try {
      const ok = await apiRemoveNotification(id);
      if (ok) queryClient.invalidateQueries({ queryKey: ["myNotifications"] });
    } catch (_) { }
  };

  const accentColors = {
    blue: "bg-blue-500",
    violet: "bg-violet-500",
    sky: "bg-sky-500",
  };
  const isResellerRole = userRole === "RESELLER" || userRole === "STAFF_RESELLER";
  const notificationsPage = isMerchantRole
    ? "MerchantNotifications"
    : isResellerRole
      ? "ResellerNotifications"
      : "Notifications";

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className={`relative bg-white ${className || ''}`}>
          <Bell className="h-5 w-5 border-white  " />
          {unreadCount > 0 && (
            <span className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 ${accentColors[accentColor] || accentColors.blue} text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none`}>
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between p-3 border-b">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-sm">{t("notifications")}</h3>
            {unreadCount > 0 && (
              <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">
                {unreadCount} {t("newNotifications") || "New"}
              </span>
            )}
          </div>
          <span />
        </div>

        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="p-6 flex justify-center items-center gap-2 text-slate-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("loading")}
            </div>
          ) : unreadNotifications.length === 0 ? (
            <div className="p-6 text-center text-slate-500 text-sm">
              {t("noUnreadNotifications") || "No unread notifications"}
            </div>
          ) : (
            unreadNotifications.map((notification) => {
              const config = typeConfig[notification.type?.toLowerCase()] || typeConfig.info;
              const Icon = config.icon;

              return (
                <div
                  key={notification.id}
                  onClick={() => markAsRead(notification.id)}
                  className={`flex items-start gap-3 p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0 transition-colors ${!notification.read ? "bg-blue-50/50" : ""
                    }`}
                >
                  <div className={`w-8 h-8 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`h-4 w-4 ${config.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm font-medium ${!notification.read ? "text-slate-900" : "text-slate-600"}`}>
                        {notification.title}
                      </p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeNotification(notification.id);
                        }}
                        className="text-slate-400 hover:text-slate-600 p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                      {notification.message}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-1">
                      {format(notification.time, "h:mm a")}
                    </p>
                  </div>
                  {!notification.read && (
                    <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="p-2 border-t bg-slate-50">
          <Link
            to={createPageUrl(notificationsPage)}
            onClick={() => setOpen(false)}
            className="w-full text-center text-xs text-slate-600 hover:text-slate-900 py-1.5 font-medium hover:bg-slate-100 rounded transition-colors block"
          >
            {t("viewAllNotifications")}
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}